import { FormEvent, useEffect, useState } from 'react';
import type { User } from '@core/generated/gql-types';
import { useNoteStore } from '@core/note/useNoteStore';
import { actionFocusRing2ClassName } from '@core/theme/actionTheme.util';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import { Spinner } from '@/components/shared/Spinner';
import NoteItem from './notes/NoteItem';

const MAX_NOTE_CONTENT_LENGTH = 500;

interface NotesProps {
  worker: User;
}

const Notes = ({ worker }: NotesProps) => {
  const {
    createNote,
    deleteNote,
    deletingNoteId,
    ensureNotesForObject,
    error,
    getNotesForObject,
    isCreating,
    isLoading,
  } = useNoteStore();
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [content, setContent] = useState('');
  const notes = getNotesForObject(worker.id);
  const canSave = content.trim().length > 0;

  useEffect(() => {
    void ensureNotesForObject(worker.id).catch(() => undefined);
  }, [ensureNotesForObject, worker.id]);

  const handleCancel = () => {
    setContent('');
    setIsAddingNote(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSave) {
      return;
    }

    try {
      await createNote(worker.id, content);
      setContent('');
      setIsAddingNote(false);
      toast.success('Note added');
    } catch {
      toast.error('Unable to save note');
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote(noteId);
      toast.success('Note deleted');
    } catch {
      toast.error('Unable to delete note');
    }
  };

  return (
    <section className="rounded-lg bg-white shadow-sm">
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-xl font-semibold text-gray-900">Notes</h2>
      </div>
      <div className="p-6">
        {isAddingNote ? (
          <form onSubmit={handleSubmit}>
            <div className="relative">
              <textarea
                aria-label="Add note"
                autoFocus
                className={`min-h-28 w-full resize-y rounded-md border border-gray-300 px-4 py-3 pr-20 text-sm text-gray-900 placeholder-gray-400 ${actionFocusRing2ClassName}`}
                maxLength={MAX_NOTE_CONTENT_LENGTH}
                onChange={(event) => setContent(event.target.value)}
                placeholder="Add note..."
                value={content}
              />
              <span className="absolute right-3 bottom-3 text-xs text-gray-500">
                {content.length} / {MAX_NOTE_CONTENT_LENGTH}
              </span>
            </div>
            <div className="mt-3 flex justify-end gap-3">
              <Button
                className="!py-2 text-sm"
                disabled={isCreating}
                onClick={handleCancel}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                className="!py-2 text-sm"
                disabled={!canSave}
                isLoading={isCreating}
                type="submit"
              >
                Save
              </Button>
            </div>
          </form>
        ) : (
          <Button
            className="!py-2 text-sm"
            onClick={() => setIsAddingNote(true)}
          >
            Add Note
          </Button>
        )}

        <div className={isAddingNote ? 'mt-6' : 'mt-8'}>
          {isLoading && notes.length === 0 ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : error && notes.length === 0 ? (
            <p className="py-4 text-sm text-red-600">{error}</p>
          ) : notes.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">
              No notes have been added yet.
            </p>
          ) : (
            <div className="space-y-6">
              {notes.map((note) => (
                <NoteItem
                  key={note.id}
                  isDeleting={deletingNoteId === note.id}
                  note={note}
                  onDelete={handleDeleteNote}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Notes;
